import { useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { usePushNotifications } from 'src/hooks/use-push-notifications';
import { useRouter } from 'src/routes/hooks';
// components
import { useSettingsContext } from 'src/components/settings';
//
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const router = useRouter();

  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  // --- Web Push Notifications ---
  const loginDetails = (() => {
    try { return JSON.parse(localStorage.getItem('loginDetails') || '{}'); } catch { return {}; }
  })();
  const userId = loginDetails?.logindata?.id || loginDetails?.id || '';
  const userType = loginDetails?.logindata?.user_type || loginDetails?.user_type || 'admin';
  const token = loginDetails?.token || loginDetails?.logindata?.token || '';
  const companyId = loginDetails?.logindata?.company_id || loginDetails?.company_id || '';

  usePushNotifications(userId, userType, token, companyId);

  // When a push arrives while the tab is in the foreground (service worker posts PUSH_RECEIVED),
  // reload the notification popover by dispatching a custom event the bell can listen to.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const handleMessage = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        window.dispatchEvent(new CustomEvent('erp:push-received'));
      }
      if (event.data?.type === 'NOTIFICATION_NAVIGATE' && event.data?.url) {
        router.push(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [router]);
  // --- End Web Push ---

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>{children}</Main>
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>{children}</Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {renderNavVertical}

        <Main>{children}</Main>
      </Box>
    </>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
